import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  type RouterStateSnapshot,
  type UrlTree,
  Router,
} from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import { OrganisationService } from '../organisation.service';
import { map } from 'rxjs/operators';
import {
  DAYS_OF_WEEK,
  DayOfWeek,
  filterUndefined,
  isSameRef,
  Time24hrType,
  to24hrTime,
  toDayOfWeek,
} from '@principle-theorem/shared';
import { ManagementService } from './management.service';
import { type IUserRestrictionDay } from '@principle-theorem/principle-core/interfaces';
import { keyBy } from 'lodash';
import * as moment from 'moment-timezone';

export enum UserRestrictedReason {
  Time = 'time',
  Day = 'day',
  IP = 'ip',
}

export interface IUserRestrictedQueryParams {
  reason: UserRestrictedReason;
  restrictedUntil?: Time24hrType;
  restrictedUntilDay?: DayOfWeek;
}

export interface INextUserRestrictionOpening {
  day: DayOfWeek;
  time: Time24hrType;
  isToday: boolean;
}

@Injectable()
export class IsTimeRestrictedGuard {
  constructor(
    private _router: Router,
    private _organisation: OrganisationService,
    private _management: ManagementService
  ) {}

  canActivate(
    _: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return combineLatest([
      this._organisation.practice$.pipe(filterUndefined()),
      this._management.user$,
      this._organisation.user$,
    ]).pipe(
      map(([practice, managementUser, user]) => {
        const userRestrictions = practice.restrictions?.userRestrictions;
        if (
          managementUser ||
          !userRestrictions?.isEnabled ||
          !userRestrictions?.restrictions.length
        ) {
          return true;
        }

        const found = userRestrictions.restrictions.find((restriction) =>
          isSameRef(restriction.user, user)
        );
        if (!found) {
          return true;
        }

        // TODO: defensive check to prevent user lockout before migration
        // has completed. Can be removed once migration is complete.
        const schedule = found.schedule ?? [];
        if (!schedule.length) {
          return true;
        }

        const timezone = practice.settings.timezone;
        const now = moment().tz(timezone);
        const today = toDayOfWeek(now, timezone);

        const scheduledDay = schedule.find((day) => day.day === today);
        if (!scheduledDay) {
          const queryParams: IUserRestrictedQueryParams = {
            reason: UserRestrictedReason.Day,
          };
          return this._router.createUrlTree(['/user-restricted'], {
            queryParams,
          });
        }

        const currentTime = to24hrTime(now);
        if (
          isWithinTimeRange(
            currentTime,
            scheduledDay.allowedFrom,
            scheduledDay.allowedTo
          )
        ) {
          return true;
        }

        const opening = nextOpening(today, currentTime, schedule);
        const queryParams: IUserRestrictedQueryParams = {
          reason: UserRestrictedReason.Time,
          restrictedUntil: opening?.time,
          restrictedUntilDay: opening?.isToday ? undefined : opening?.day,
        };
        return this._router.createUrlTree(['/user-restricted'], {
          queryParams,
        });
      })
    );
  }
}

export function nextOpening(
  today: DayOfWeek,
  now: Time24hrType,
  schedule: IUserRestrictionDay[]
): INextUserRestrictionOpening | undefined {
  const scheduleByDay = keyBy(schedule, 'day');

  const todaysWindow = scheduleByDay[today];
  if (todaysWindow && now < todaysWindow.allowedFrom) {
    return { day: today, time: todaysWindow.allowedFrom, isToday: true };
  }

  const todayIndex = DAYS_OF_WEEK.indexOf(today);
  const laterThisWeek = DAYS_OF_WEEK.slice(todayIndex + 1);
  const earlyNextWeek = DAYS_OF_WEEK.slice(0, todayIndex + 1);
  const upcomingDays = [...laterThisWeek, ...earlyNextWeek];

  const nextDay = upcomingDays.find((day) => scheduleByDay[day]);
  if (!nextDay) {
    return;
  }

  return {
    day: nextDay,
    time: scheduleByDay[nextDay].allowedFrom,
    isToday: false,
  };
}

export function isWithinTimeRange(
  now: Time24hrType,
  from: Time24hrType,
  to: Time24hrType
): boolean {
  return now >= from && now <= to;
}
